import React, { Fragment } from "react";
import { Box, Typography } from "@mui/material";
import {
  LanguageOutlined,
  SensorsOutlined,
  SettingsSuggestOutlined,
  StorageOutlined,
} from "@mui/icons-material";

import ContentSection from "../components/ContentSection";
import LinkButton from "../components/ui/LinkButton";
import CompactCard from "../components/CompactCard";

import DataCenterServers from "../assets/images/DataCenterServers.jpg";
import MountainsTreeLake from "../assets/images/MountainTreesLake.jpg";
import InfographicBiogasToBiomethaneProcess from "../assets/images/InfographicBiogasToBiomethaneProcess.jpg";
import BackgroundImageBox from "../components/ui/BackgroundImageBox";
import HeroSectionV2 from "../components/HeroSectionV2";

function HomePage() {
  // const theme = useTheme();

  return (
    <Fragment>
      <HeroSectionV2
        heading="Investing in Sustainable Development"
        subheading="Shaping a cleaner, greener tomorrow with innovative technology and sustainable energy solutions."
      >
        <Box display="flex" gap={6} flexDirection={{ xs: "column", lg: "row" }}>
          <Box sx={{ flex: 1, flexShrink: 0 }}>
          <Typography
              color="#001b35"
              variant="h3"
              sx={{ flex: 1, flexShrink: 0 }}
            >
              We are implementing innovative projects to improve the future.
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              Flamestone is a forward-thinking company committed to green
              technologies and driving solutions that promote sustainability.
              With a heritage of technological innovation, we integrate advanced
              hardware, electronics, and software systems to deliver clean,
              efficient, and environmentally friendly processes.
            </Typography>
            <Box
              sx={{
                display: "flex",
                marginTop: 4,
                gap: 1,
              }}
            >
              <LinkButton to="/biogas" size="large">
                Read More
              </LinkButton>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              backgroundImage: `url(${MountainsTreeLake})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              borderRadius: 8,
              minHeight: "200px",
            }}
          />
        </Box>
      </HeroSectionV2>
      <ContentSection sx={{ marginTop: {lg: 6} }}>
        <Box
          sx={{
            margin: "0 auto",
            textAlign: "center",
            maxWidth: "900px",
          }}
        >
          <Typography
            color="#001b35"
            variant="h2"
            sx={{ flex: 1, flexShrink: 0 }}
          >
            Transforming Biogas to Biofuel Technology
          </Typography>
          <Typography color="#001b35" variant="body1" marginTop={3}>
            Flamestone’s investment projects include converting biogas into
            biomethane, BioLNG and BioCNG providing sustainable alternatives to
            traditional fuels. We ensure precision, monitoring efficiency, and
            optimised operations to transform waste into valuable resources.
          </Typography>
          <img
            alt="Infographic of the biogas-to-biomethane process"
            src={InfographicBiogasToBiomethaneProcess}
            style={{
              maxWidth: "100%",
              maxHeight: '450px',
              marginTop: "48px",
            }}
          />
          <Box
            sx={{
              marginTop: 4,
              gap: 1,
            }}
          >
            <LinkButton to="/biogas" size="large">
              Learn about Biogas Solutions
            </LinkButton>
          </Box>
        </Box>
      </ContentSection>
      <BackgroundImageBox image={DataCenterServers}>
        <Box sx={{ flex: 1, flexShrink: 0 }}>
          <Typography color="#001b35" variant="h2" marginBlockStart={2}>
            Green Technology Solutions at the Core
          </Typography>
          <Typography
            color="#001b35"
            variant="body1"
            marginTop={2}
          >
            Expanding beyond energy systems, Flamestone specialises in custom
            electronics and software tailored for sustainable applications. From
            IoT-enabled devices to AI-driven analytics, our solutions empower
            industries to reduce emissions, optimise processes, and achieve
            sustainability targets.
          </Typography>
        </Box>
        <Typography color="#001b35" variant="h6" marginBlockStart={6}>
          Key Offerings
        </Typography>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: { xs: "column", lg: "row" },
            flexShrink: 0,
            gap: 4,
            marginTop: 4,
          }}
        >
          {[
            {
              heading: "IoT Integration for real-time energy monitoring.",
              icon: SensorsOutlined,
            },
            {
              heading: "Advanced datacentre and hardware solutions.",
              icon: StorageOutlined,
            },
            {
              heading: "AI-powered automation tools.",
              icon: SettingsSuggestOutlined,
            },
            {
              heading: "Localised, user-friendly software interfaces.",
              icon: LanguageOutlined,
            },
          ].map((sector) => (
            <Box
              key={sector.heading}
              boxSizing="border-box"
              width={{ sx: "100%", lg: "25%" }}
            >
              <CompactCard
                key={sector.heading}
                icon={sector.icon}
                title={sector.heading}
                sx={{ textAlign: "left" }}
              />
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            marginTop: 6,
            gap: 1,
          }}
        >
          <LinkButton to="/energy" size="large">
            Explore Energy Tech
          </LinkButton>
        </Box>
      </BackgroundImageBox>
      <ContentSection>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "center",
            margin: "32px auto",
            gap: 6,
          }}
        >
          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <Typography color="#001b35" variant="h3">
              A Trusted Partner in Green Innovation
            </Typography>
            <Typography color="#001b35" variant="body1" marginTop={2}>
              With over 13 years of expertise, Flamestone is trusted by industry
              leaders to deliver innovative and impactful solutions. Partnering
              with companies like Foxconn, Intel, Pegatron, and MGE Data, we
              ensure cutting-edge solutions that align with environmental goals.
            </Typography>
          </Box>

          <Box sx={{ flex: 1, flexShrink: 0 }}>
            <Typography color="#001b35">
              <Typography variant="subtitle1">
                Why Choose Flamestone?
              </Typography>
            </Typography>
            <Box
              sx={{
                display: "flex",
                direction: "row",
                flexWrap: "wrap",
                marginTop: 3,
                gap: 1,
              }}
            >
              {[
                "Integrated Solutions tailored to specific needs.",
                "Proven Track Record in energy and technology sectors.",
                "Sustainability Commitment driving every project.",
              ].map((need) => (
                <Typography key={need}>✔ {need}</Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </ContentSection>
    </Fragment>
  );
}

export default HomePage;
